import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { useAuth } from 'src/state/auth';
import { TableUserItem } from 'src/common';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { OrganisationQuery, Role, useGrantAdminMutation, useRemoveUserMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';
import { getRoleLabel } from 'src/api/types';

type OrgUser = NonNullable<OrganisationQuery['organisation']>['users'][number];

type UseColumns = (a: { data?: OrgUser[]; orgId: string; setProfileModalId: (id: number | null) => void }) => {
  columns: ColumnDef<OrgUser>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = ['name', 'email', 'role'];

export const useMembersColumns: UseColumns = ({ data = [], orgId, setProfileModalId }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.orgMembers}-columns`, defaultColumns);
  const { profile } = useAuth();
  const { refetchQuery } = useCache();

  const resetCache = () => refetchQuery({ include: ['organisation', 'usersEmails'] });

  const [grantAdmin] = useGrantAdminMutation({
    onCompleted: resetCache,
  });

  const [removeUser] = useRemoveUserMutation({ onCompleted: resetCache });

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm];

    const list: ColumnDef<OrgUser>[] = [
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.orgUnitIdentity?.name,
        cell: ({ row }) => {
          return (
            <TableUserItem
              profilePhotoPath={row.original.profilePhotoPath}
              name={`${row.original.orgUnitIdentity?.name} ${+profile?.user.id! === +row.original.id ? '(You)' : ''}`}
              interactive={{
                email: row.original.email,
                scope: 'organisation',
                orgId: orgId,
              }}
            />
          );
        },
        sortingFn: sortingFns.text,
      },
      {
        id: 'email',
        header: 'Email',
        accessorFn: (row) => row.email || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'role',
        header: 'Role',
        accessorFn: (row) => row.orgUnitIdentity?.baseRole,
        cell: ({ row }) => getRoleLabel(row.original.orgUnitIdentity?.baseRole),
        sortingFn: sortingFns.text,
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const canRemoveUser = row.original.organisationUserActions?.canRemoveUser;
          const canGrantAdmin = row.original.organisationUserActions?.canGrantAdmin;
          const canEditProfile = row.original.organisationUserActions?.canEditProfile;

          const onRemoveUserClick = canRemoveUser
            ? () => removeUser({ variables: { organisationId: orgId, userId: +row.original.id } })
            : undefined;

          const onMakeAdminClick = canGrantAdmin
            ? () =>
                grantAdmin({
                  variables: {
                    organisationId: orgId,
                    userId: +row.original.id,
                  },
                })
            : undefined;

          const onEditProfileClick = canEditProfile
            ? () => setProfileModalId(+row.original.id)
            : undefined;

          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm
                onRemoveUserClick={onRemoveUserClick}
                onChangePermissions={onEditProfileClick}
                onMakeAdminClick={onMakeAdminClick}
              />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, profile]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.orgMembers}
      options={[
        { key: 'name', title: 'Name' },
        { key: 'email', title: 'Email' },
        { key: 'role', title: 'Role' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
  };
};
